import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Box,
  Typography,
} from "@mui/material";
import moment from "moment";
// import { invoiceDescriptionByLocation } from "../../Data/data";

const InvoiceDescription = ({
  vendorId,
  invoiceDetailsByLocation,
  invoiceDescriptionData,
  chargedPlateCount,
  totalInvoice,
  totalMealConsumed,
  setChargedPlateCount,
  setTotalInvoice,
  setTotalMealConsumed,
  mealType,
  location,
  startDate,
  perPlatePrice,
  minGuarantee,
  invoiceDescriptionByLocation,
}) => {
  const [invoiceDescription, setInvoiceDescription] = useState([]);
  const [minGuaranteeMeal, setMinGuaranteeMeal] = useState(0);

  const formatNumberIndian = useCallback((num) => {
    return num.toLocaleString("en-IN");
  }, []);

  const invoiceDescriptionJson = useCallback(
    (data) => {
      const merged = [];
      data?.forEach((item) => {
        const key = `${item.mealDate}_${item.dayOfWeek?.trim()}`;

        const existingIndex = merged.findIndex(
          (entry) => `${entry.mealDate}_${entry.Day?.trim()}` === key
        );

        const vendorPerPlatePrice = item.vendor_per_plate_price
          ? item.vendor_per_plate_price
          : perPlatePrice;

        const vendorMinimumGuarantee = item.vendor_minimum_guarantee
          ? item.vendor_minimum_guarantee
          : minGuarantee;

        if (existingIndex === -1) {
          merged.push({
            mealDate: item.mealDate,
            Day: item.dayOfWeek || "",
            meal_display_name: item.meal_display_name || "",
            mealType: item.diet_name || "",
            details: [...item.details],
            vendorPerPlatePrice,
            vendorMinimumGuarantee,
          });
        } else {
          const ex = merged[existingIndex];

          if (
            item.meal_display_name &&
            !ex.meal_display_name.includes(item.meal_display_name)
          ) {
            ex.meal_display_name += ` & ${item.meal_display_name}`;
          }

          if (item.diet_name && !ex.mealType.includes(item.diet_name)) {
            ex.mealType += ` & ${item.diet_name}`;
          }

          const existingDiet = `${item.diet_name}`;
          const newDetails = [...ex.details];

          item.details.forEach((newItem) => {
            const match = newDetails.find(
              (d) =>
                d.mealType === newItem.mealType &&
                item.diet_name === ex.mealType
            );
            if (match) {
              match.mealCount += newItem.mealCount;
            } else {
              newDetails.push({ ...newItem });
            }
          });

          ex.details = newDetails;

          ex.vendorPerPlatePrice += ` & ${vendorPerPlatePrice}`;
          ex.vendorMinimumGuarantee += ` & ${vendorMinimumGuarantee}`;
        }
      });
      setInvoiceDescription(merged);
    },
    [invoiceDetailsByLocation?.vendorId, minGuarantee, perPlatePrice]
  );

  useEffect(() => {
    if (mealType === 1 || (mealType === 2 && location === 1)) {
      invoiceDescriptionJson(invoiceDescriptionData);
    } else if (mealType !== 1 && location !== 1) {
      let uniqueMealDataList = invoiceDescriptionData;
      if (uniqueMealDataList) invoiceDescriptionJson(uniqueMealDataList);
    }
  }, [
    invoiceDescriptionByLocation,
    invoiceDescriptionData,
    invoiceDescriptionJson,
    location,
    mealType,
  ]);

  useEffect(() => {
    let min_gur = 0;
    invoiceDescription.forEach((curr) => {
      String(curr.vendorMinimumGuarantee || "")
        .split("&")
        .forEach((p) => {
          min_gur += parseInt(p.trim() || "0", 10);
        });
    });
    setMinGuaranteeMeal(min_gur);
  }, [invoiceDescription]);

  const splitCountsByRatio = (minGuaranteeStr, chargedCounts) => {
    // Convert the minGuarantee string to an array of numbers
    const minGuarantees = String(minGuaranteeStr ?? "")
      .split(/\s*&\s*/)
      .map((n) => parseInt(n.trim(), 10) || 0);

    // Return the minGuarantees array without any comparison
    return minGuarantees;
  };

  useEffect(() => {
    let total = 0;
    invoiceDescription.forEach((curr) => {
      const totalCount =
        curr.details?.reduce((a, d) => a + d.mealCount, 0) || 0;
      const chargedParts = splitCountsByRatio(
        curr.vendorMinimumGuarantee,
        totalCount
      );
      total += chargedParts.reduce((a, b) => a + b, 0);
    });
    setChargedPlateCount(total);
  }, [invoiceDescription, minGuarantee, setChargedPlateCount]);

  useEffect(() => {
    if (!invoiceDescription?.length) {
      setTotalMealConsumed(0);
      setTotalInvoice(formatNumberIndian(0));
      return;
    }

    let grandTotal = 0;
    let totalConsumedPlates = 0;

    invoiceDescription.forEach((curr) => {
      const totalMealCount =
        curr.details?.reduce((a, d) => a + d.mealCount, 0) || 0;
      // totalConsumedPlates = totalMealCount;

      const prices = String(curr.vendorPerPlatePrice ?? "")
        .split(/\s*&\s*/)
        .map((p) => parseInt(p.trim(), 10) || 0);

      const chargedParts = splitCountsByRatio(
        curr.vendorMinimumGuarantee,
        totalMealCount
      );
      chargedParts.forEach((count, i) => {
        grandTotal += count * prices[i];
      });
    });

    // setTotalMealConsumed(totalConsumedPlates);
    setTotalInvoice(formatNumberIndian(grandTotal));
  }, [
    formatNumberIndian,
    invoiceDescription,
    setTotalInvoice,
    setTotalMealConsumed,
  ]);

  const renderAmountPerPlate = (
    minGuaranteeStr,
    perPlatePriceStr,
    details = []
  ) => {
    if (!details?.length) return 0;

    const guaranteeParts = String(minGuaranteeStr ?? "")
      .split(/\s*&\s*/)
      .map((n) => parseInt(n.trim(), 10) || 0);

    const priceArr = String(perPlatePriceStr ?? "")
      .split(/\s*&\s*/)
      .map((n) => parseInt(n.trim(), 10) || 0);

    const groupCount = guaranteeParts?.length || 1;
    const groupSize = Math.floor(details?.length / groupCount);

    const actualCounts = Array.from({ length: groupCount }, (_, i) => {
      const sliceStart = i * groupSize;
      const sliceEnd = sliceStart + groupSize;
      return details
        .slice(sliceStart, sliceEnd)
        .reduce((sum, d) => sum + (d.mealCount || 0), 0);
    });

    const chargedCounts = splitCountsByRatio(minGuaranteeStr, actualCounts);

    return chargedCounts.reduce(
      (sum, charged, idx) => sum + charged * (priceArr[idx] || 0),
      0
    );
  };
  useEffect(() => {
    const totalAmt = invoiceDescription.reduce((sum, row) => {
      return (
        sum +
        renderAmountPerPlate(
          row.vendorMinimumGuarantee,
          row.vendorPerPlatePrice,
          row.details
        )
      );
    }, 0);

    setTotalInvoice(formatNumberIndian(totalAmt));
  }, [
    invoiceDescription,
    // renderAmountPerPlate,
    formatNumberIndian,
    setTotalInvoice,
  ]);

  const renderTotalConsumedPlates = (item) => {
    // If there are no details or it's not an array, return empty
    if (!Array.isArray(item.details) || item.details.length === 0) return "";

    let totalMealCount = 0;
    let previousMealType = null;

    // Loop through the details to sum the meal counts based on mealType
    item.details.forEach((detail) => {
      if (previousMealType && previousMealType !== detail.mealType) {
        // If the mealType is different, sum the counts
        totalMealCount += detail.mealCount || 0;
      } else {
        // If mealType is the same, just take the first mealCount
        totalMealCount = detail.mealCount || 0;
      }
      previousMealType = detail.mealType;
    });

    // Return the total meal count
    return totalMealCount || "";
  };

  const renderConsumedInfo = (data) => {
    const mealTypeCounts = {
      self: 0,
      "ctr-self": 0,
      guest: 0,
    };

    data.forEach((item) => {
      const type = item.mealType?.toLowerCase();
      if (mealTypeCounts.hasOwnProperty(type)) {
        mealTypeCounts[type] = item.mealCount || 0;
      }
    });
    return (
      <Box display="flex" gap={2}>
        <Box>
          <span>Emp: </span>
          <span>{mealTypeCounts.self}</span>
        </Box>
        <Box>
          <span>Cont: </span>
          <span>{mealTypeCounts["ctr-self"]}</span>
        </Box>
        <Box>
          <span>Gue: </span>
          <span>{mealTypeCounts.guest}</span>
        </Box>
      </Box>
    );
  };
  const renderChargedPlateCount = (minGuarantee, details = []) => {
    if (!Array.isArray(details) || !details?.length) return "";
    const counts = details.map((d) => d.mealCount || 0);
    return splitCountsByRatio(minGuarantee, counts).join(" & ");
  };

  const totalMinGuarantee = invoiceDescription.reduce((sum, curr) => {
    const parts = String(curr.vendorMinimumGuarantee ?? "")
      .split(/\s*&\s*/)
      .map((n) => parseInt(n.trim(), 10) || 0);
    return sum + parts.reduce((a, b) => a + b, 0);
  }, 0);

  const totalConsumedPlates = invoiceDescription.reduce((sum, curr) => {
    // Create a set to track the unique mealTypes for each day
    // const mealDateKey = curr.mealDate;
    const uniqueMealTypes = new Set();

    curr.details.forEach((detail) => {
      // Only add mealCount if the mealType hasn't been added for this mealDate
      if (!uniqueMealTypes.has(detail.mealType)) {
        sum += detail.mealCount || 0; // Add the meal count to the total
        uniqueMealTypes.add(detail.mealType); // Mark this mealType as processed
      }
    });

    return sum;
  }, 0);

  const totalChargedPlates = invoiceDescription.reduce((sum, curr) => {
    const counts = curr.details.map((d) => d.mealCount || 0);
    const chargedArr = splitCountsByRatio(curr.vendorMinimumGuarantee, counts);
    return sum + chargedArr.reduce((a, b) => a + b, 0);
  }, 0);

  const showDayAndMealType =
    mealType === 2 && (location === 2 || location === 3);

  return (
    <Box mt={2}>
      {/* Title */}
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{
          border: "1px solid #C5C9CF",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          borderBottom: "unset",
          padding: "10px",
        }}
      >
        {`${
          startDate
            ? moment(startDate?.$d).format("MMMM YYYY")
            : moment(new Date()).subtract(1, "month").format("MMMM YYYY")
        } Invoice description`}
      </Typography>

      {/* Table */}
      <TableContainer>
        <Table sx={{ minWidth: 900, borderCollapse: "collapse" }}>
          {/* Table Head */}
          <TableHead>
            <TableRow sx={{ backgroundColor: "#93CFEC33" }}>
              {[
                "Sr No.",
                "Date",
                ...(showDayAndMealType ? ["Day", "Meal Type"] : []),
                "Per plate cost",
                "Min guarantee",
                "Total consumed plate",
                "Consumed info",
                "Charged plate count",
                "Total amount for plate count",
              ].map((header) => (
                <TableCell
                  key={header}
                  sx={{ border: "1px solid #ccc", fontWeight: "bold" }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {invoiceDescription?.length > 0 ? (
              invoiceDescription.map((item, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ border: "1px solid #ccc", color: "black" }}>
                    {i + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #ccc",
                      color: "black",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {moment(item.mealDate).format("MMMM DD")}
                  </TableCell>

                  {showDayAndMealType && (
                    <TableCell
                      sx={{ border: "1px solid #ccc", color: "black" }}
                    >
                      {item?.Day}
                    </TableCell>
                  )}
                  {showDayAndMealType && (
                    <TableCell
                      sx={{ border: "1px solid #ccc", color: "black" }}
                    >
                      {item?.mealType
                        ? item?.mealType
                            .split(" & ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(" & ")
                        : ""}
                    </TableCell>
                  )}

                  <TableCell sx={{ border: "1px solid #ccc", color: "black" }}>
                    ₹ {item?.vendorPerPlatePrice}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #ccc", color: "black" }}>
                    {item?.vendorMinimumGuarantee}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #ccc", color: "black" }}>
                    {renderTotalConsumedPlates(item)}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #ccc", color: "black" }}>
                    {renderConsumedInfo(item.details)}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #ccc",
                      color:
                        renderChargedPlateCount(item.details) > minGuarantee
                          ? "red"
                          : "black",
                    }}
                  >
                    {renderChargedPlateCount(
                      item?.vendorMinimumGuarantee,
                      item?.details
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #ccc",
                      color:
                        renderAmountPerPlate(item.details) >
                        minGuarantee * perPlatePrice
                          ? "red"
                          : "black",
                    }}
                  >
                    ₹{" "}
                    {renderAmountPerPlate(
                      item?.vendorMinimumGuarantee,
                      item?.vendorPerPlatePrice,
                      item?.details
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={showDayAndMealType ? 10 : 8}
                  align="center"
                  sx={{
                    border: "1px solid #ccc",
                    color: "gray",
                    fontWeight: 500,
                    textAlign: "center",
                    py: 4,
                  }}
                >
                  No data available for the following month
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {/* Table Footer */}
          <TableFooter>
            {invoiceDescription.length > 0 && (
              <TableRow>
                <TableCell
                  colSpan={showDayAndMealType ? 5 : 3}
                  sx={{ border: "1px solid #ccc" }}
                >
                  <Typography
                    textAlign="end"
                    sx={{ color: "black", fontWeight: "600" }}
                  >
                    Total:
                  </Typography>
                </TableCell>

                <TableCell sx={{ border: "1px solid #ccc" }}>
                  <Typography sx={{ color: "black", fontWeight: "600" }}>
                    {totalMinGuarantee}
                  </Typography>
                </TableCell>

                <TableCell sx={{ border: "1px solid #ccc" }}>
                  <Typography sx={{ color: "black", fontWeight: "600" }}>
                    {totalConsumedPlates}
                  </Typography>
                </TableCell>

                <TableCell sx={{ border: "1px solid #ccc" }}></TableCell>

                <TableCell sx={{ border: "1px solid #ccc" }}>
                  <Typography sx={{ color: "black", fontWeight: "600" }}>
                    {totalChargedPlates}
                  </Typography>
                </TableCell>

                <TableCell sx={{ border: "1px solid #ccc" }}>
                  <Typography sx={{ color: "black", fontWeight: "600" }}>
                    ₹ {totalInvoice}.00
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InvoiceDescription;
